import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { RelatedPartyEntity, ReportData } from '../tokens/types';
import { Section, Row, Box, T, Divider } from '../primitives/Universal';
import { ShareholdingBlock } from './Shareholding';
import { PdfInlineInsights, ScreenInlineInsights } from './InsightsInline';
import { useRenderTarget } from '../primitives/RenderTarget';
import { fontFamily, typeScale } from '../tokens/designTokens';
import { fmtDate } from './formatUtils';

// ──────────────────────────────────────────────────────────────────────────────
// Structure & Relations — tabular layout per Figma 32:100440.
// 3 columns: NAME / RELATION / RELATION TO.
// Bucket header rows ("DIRECTORS · 4") span full width; primary entity rows are
// bold; linked-entity rows nest under their parent with an indent and muted tint.
// Below the table the existing Shareholding pattern is rendered inline (verbatim).
// ──────────────────────────────────────────────────────────────────────────────

type GroupKey = 'directors' | 'shareholders' | 'former' | 'holdings' | 'subsidiaries' | 'associates' | 'jointVentures' | 'sisterConcerns';

const GROUPS: Array<{ key: GroupKey; label: string; childRelation: string }> = [
    { key: 'directors', label: 'Directors', childRelation: 'Common Director' },
    { key: 'shareholders', label: 'Shareholders', childRelation: 'Common Shareholder' },
    { key: 'holdings', label: 'Holdings', childRelation: 'Common Holding' },
    { key: 'subsidiaries', label: 'Subsidiaries', childRelation: 'Common Subsidiary' },
    { key: 'associates', label: 'Associates', childRelation: 'Common Associate' },
    { key: 'jointVentures', label: 'Joint ventures', childRelation: 'Common JV' },
    { key: 'sisterConcerns', label: 'Sister concerns', childRelation: 'Common Director' },
    { key: 'former', label: 'Former directors', childRelation: 'Common Director' },
];

// Token palette for the table. Kept local — these values are tuned to the Figma spec.
const COLORS = {
    headerBorder: '#1F2937', // top + bottom border on the header row
    rowBorder: '#F0EFE9', // soft hairline between body rows
    bucketBg: '#FAFAF7', // soft tint for bucket header + nested rows
    nameStrong: '#1F2937', // bold primary name
    nameSoft: '#6B7280', // nested/muted name
    headText: '#6B7280', // header column labels
    relationInk: '#1F2937', // primary relation/role text
    relationSoft: '#6B7280', // nested relation text
};

// Column widths sum to ~100% of the inner section width. Numbers are flex-like proportions
// rather than fixed widths so the table fills the section in both PDF and screen contexts.
const COL_FLEX = { name: 50, relation: 22, relationTo: 28 } as const;

// ── Cell shells ──────────────────────────────────────────────────────────────

type CellProps = {
    flex: number;
    children?: React.ReactNode;
    paddingLeft?: number;
    bg?: string;
    bold?: boolean;
    muted?: boolean;
    /** Header-style: uppercase + tracking + smaller text */
    head?: boolean;
};

const Cell: React.FC<CellProps> = ({ flex, children, paddingLeft, bg, bold, muted, head }) => {
    const target = useRenderTarget();
    if (target === 'pdf') {
        const color = muted ? COLORS.nameSoft : head ? COLORS.headText : COLORS.nameStrong;
        return (
            <View
                style={{
                    flex,
                    paddingTop: 4,
                    paddingBottom: 4,
                    paddingRight: 6,
                    paddingLeft: 6 + (paddingLeft || 0),
                    backgroundColor: bg,
                }}
            >
                <Text
                    style={{
                        fontSize: head ? typeScale.micro : typeScale.caption,
                        color,
                        fontFamily: head || bold ? fontFamily.bodyBold : fontFamily.body,
                        textTransform: head ? 'uppercase' : 'none',
                        letterSpacing: head ? 0.7 : 0,
                        lineHeight: 1.35,
                    }}
                >
                    {children}
                </Text>
            </View>
        );
    }
    // Screen
    return (
        <div
            style={{
                flex: `${flex} 1 0`,
                padding: '6px 10px',
                paddingLeft: paddingLeft != null ? 10 + paddingLeft : 10,
                background: bg,
                fontSize: head ? '0.66rem' : '0.78rem',
                color: muted ? COLORS.nameSoft : head ? COLORS.headText : COLORS.nameStrong,
                fontWeight: head ? 600 : bold ? 700 : 400,
                letterSpacing: head ? '0.06em' : 0,
                textTransform: head ? 'uppercase' : 'none',
                lineHeight: 1.4,
                wordBreak: 'break-word',
            }}
        >
            {children}
        </div>
    );
};

// Cells use the Box/T primitives for PDF and div/span for screen. The `Cell` above
// already abstracts that. The inline padding-and-bg differences are managed inside.

// ── Row variants ─────────────────────────────────────────────────────────────

const TableHeaderRow: React.FC = () => {
    const target = useRenderTarget();
    const borderTopBottom = target === 'pdf' ? undefined : `1px solid ${COLORS.headerBorder}`;
    if (target === 'pdf') {
        return (
            <Box>
                <Box height={1} background={COLORS.headerBorder} />
                <Row>
                    <Cell flex={COL_FLEX.name} head>
                        Name
                    </Cell>
                    <Cell flex={COL_FLEX.relation} head>
                        Relation
                    </Cell>
                    <Cell flex={COL_FLEX.relationTo} head>
                        Relation To
                    </Cell>
                </Row>
                <Box height={0.5} background={COLORS.headerBorder} />
            </Box>
        );
    }
    return (
        <div style={{ borderTop: `1px solid ${COLORS.headerBorder}`, borderBottom: `1px solid ${COLORS.headerBorder}` }}>
            <div style={{ display: 'flex' }}>
                <Cell flex={COL_FLEX.name} head>
                    Name
                </Cell>
                <Cell flex={COL_FLEX.relation} head>
                    Relation
                </Cell>
                <Cell flex={COL_FLEX.relationTo} head>
                    Relation To
                </Cell>
            </div>
        </div>
    );
};

const BucketHeaderRow: React.FC<{ label: string; count: number }> = ({ label, count }) => {
    const target = useRenderTarget();
    if (target === 'pdf') {
        return (
            <Box minPresenceAhead={36}>
                <Row background={COLORS.bucketBg}>
                    <Box flex={1} paddingHorizontal={6} paddingVertical={4} background={COLORS.bucketBg}>
                        <T size="caption" tone="ink00" family="bold">
                            {`${label.toUpperCase()} · ${count}`}
                        </T>
                    </Box>
                </Row>
                <Box height={0.5} background={COLORS.rowBorder} />
            </Box>
        );
    }
    return (
        <div
            style={{
                background: COLORS.bucketBg,
                borderBottom: `1px solid ${COLORS.rowBorder}`,
                padding: '6px 10px',
                fontSize: '0.78rem',
                fontWeight: 700,
                color: COLORS.nameStrong,
                letterSpacing: '0.04em',
            }}
        >
            {label.toUpperCase()} · {count}
        </div>
    );
};

const TableRow: React.FC<{
    name: string;
    relation: string;
    relationTo: string;
    bold?: boolean;
    muted?: boolean;
    bg?: string;
    indent?: number;
}> = ({ name, relation, relationTo, bold, muted, bg, indent }) => {
    const target = useRenderTarget();
    if (target === 'pdf') {
        return (
            <Box>
                <Row background={bg}>
                    <Cell flex={COL_FLEX.name} bg={bg} bold={bold} muted={muted} paddingLeft={indent}>
                        {name}
                    </Cell>
                    <Cell flex={COL_FLEX.relation} bg={bg} muted={muted}>
                        {relation}
                    </Cell>
                    <Cell flex={COL_FLEX.relationTo} bg={bg} muted={muted}>
                        {relationTo}
                    </Cell>
                </Row>
                <Box height={0.5} background={COLORS.rowBorder} />
            </Box>
        );
    }
    return (
        <div style={{ display: 'flex', background: bg, borderBottom: `1px solid ${COLORS.rowBorder}` }}>
            <Cell flex={COL_FLEX.name} bg={bg} bold={bold} muted={muted} paddingLeft={indent}>
                {name}
            </Cell>
            <Cell flex={COL_FLEX.relation} bg={bg} muted={muted}>
                {relation}
            </Cell>
            <Cell flex={COL_FLEX.relationTo} bg={bg} muted={muted}>
                {relationTo}
            </Cell>
        </div>
    );
};

// One "entity unit" = primary row + its linked-entity sub-rows. Wrapped together so
// the primary doesn't orphan from its first linked entity at a page break — but if
// a unit has many sub-rows, we let it break naturally between sub-rows by NOT
// wrapping the whole unit (only the primary + first sub-row stay together).
const EntityUnit: React.FC<{
    entity: RelatedPartyEntity;
    childRelation: string;
    primaryRelationTo: string;
}> = ({ entity, childRelation, primaryRelationTo }) => {
    const target = useRenderTarget();
    const designation = entity.designation || (entity.role ? entity.role.charAt(0) + entity.role.slice(1).toLowerCase() : 'Director');
    const linked = entity.otherEntities ?? [];
    const primaryName = entity.name || '—';
    const PrimaryRow = <TableRow name={primaryName} relation={designation} relationTo={primaryRelationTo} bold />;
    const subRows = linked.map((other, i) => (
        <TableRow
            key={`${entity.name}-sub-${i}`}
            name={other}
            relation={childRelation}
            relationTo={primaryName}
            muted
            bg={COLORS.bucketBg}
            indent={20}
        />
    ));
    if (target === 'pdf') {
        // Keep primary + first 2 linked entities together; let the rest flow.
        const keepTogether = subRows.slice(0, 2);
        const rest = subRows.slice(2);
        return (
            <>
                <Box wrap={false} minPresenceAhead={28}>
                    {PrimaryRow}
                    {keepTogether}
                </Box>
                {rest}
            </>
        );
    }
    return (
        <>
            {PrimaryRow}
            {subRows}
        </>
    );
};

// ── Main module ──────────────────────────────────────────────────────────────

const StructureRelations: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const target = useRenderTarget();
    const rp = data.relatedParties;
    const hasRelations = !!rp && GROUPS.some(({ key }) => ((rp as any)[key] as RelatedPartyEntity[] | undefined)?.length);
    const hasShareholding = !!data.shareholding && data.shareholding.rows.length > 0;
    if (!hasRelations && !hasShareholding) return null;

    const primaryRelationTo = data.identity?.orgName || '—';

    // Total parties + bucket count for the section subtitle.
    let totalParties = 0;
    const filledGroups: Array<{ key: GroupKey; label: string; childRelation: string; entities: RelatedPartyEntity[] }> = [];
    GROUPS.forEach((g) => {
        const list = (rp as any)?.[g.key] as RelatedPartyEntity[] | undefined;
        if (list?.length) {
            totalParties += list.length;
            filledGroups.push({ ...g, entities: list });
        }
    });

    const subtitle = hasRelations
        ? `${totalParties} part${totalParties === 1 ? 'y' : 'ies'} across ${filledGroups.length} bucket${
              filledGroups.length === 1 ? '' : 's'
          } · Direct connections with linked entities nested below`
        : 'Cap table snapshot';

    return (
        <Section kicker="Network" title="Structure & Relations" subtitle={subtitle} index={index} breakBefore={breakBefore}>
            {hasRelations ? (
                <>
                    {target === 'pdf' ? (
                        <Box marginTop={4}>
                            <TableHeaderRow />
                            {filledGroups.map(({ key, label, childRelation, entities }) => (
                                <Box key={key}>
                                    <BucketHeaderRow label={label} count={entities.length} />
                                    {entities.map((e, i) => (
                                        <EntityUnit
                                            key={`${e.name}-${i}`}
                                            entity={e}
                                            childRelation={childRelation}
                                            primaryRelationTo={primaryRelationTo}
                                        />
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <div style={{ marginTop: 8 }}>
                            <TableHeaderRow />
                            {filledGroups.map(({ key, label, childRelation, entities }) => (
                                <div key={key}>
                                    <BucketHeaderRow label={label} count={entities.length} />
                                    {entities.map((e, i) => (
                                        <EntityUnit
                                            key={`${e.name}-${i}`}
                                            entity={e}
                                            childRelation={childRelation}
                                            primaryRelationTo={primaryRelationTo}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            ) : null}

            {hasShareholding ? (
                <>
                    <Divider marginVertical={14} />
                    <Row align="baseline" justify="space-between" marginBottom={10}>
                        <T size="h3" family="bold" tone="ink00">
                            Shareholding pattern
                        </T>
                        <T size="caption" tone="muted">
                            As of {fmtDate(data.shareholding!.financialEndDate)}
                        </T>
                    </Row>
                    <ShareholdingBlock data={data} />
                </>
            ) : null}

            {target === 'pdf' ? <PdfInlineInsights data={data} k="relatedParty" /> : <ScreenInlineInsights data={data} k="relatedParty" />}
        </Section>
    );
};

export const relatedPartiesModule: ModuleDefinition = {
    meta: {
        id: 'relatedParties',
        title: 'Structure & Relations',
        description: 'Directors, shareholders, holdings, linked entities, and shareholding pattern.',
        dataSource: 'Related Parties graph + Shareholding graph',
    },
    hasData: (d) => {
        const rp = d.relatedParties;
        const anyParty = !!rp && GROUPS.some(({ key }) => ((rp as any)[key] ?? []).length > 0);
        const anyShareholding = !!d.shareholding && d.shareholding.rows.length > 0;
        return anyParty || anyShareholding;
    },
    Pdf: StructureRelations,
    Screen: StructureRelations,
};
